import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { Layout, Menu, Avatar, Dropdown, Button, Badge, Divider } from 'antd'
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  CalendarOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BulbOutlined,
  CarOutlined,
  ShopOutlined,
  DollarOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  ShoppingCartOutlined,
  InboxOutlined,
  WalletOutlined,
  BankOutlined
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { hasPermission, canManageDealers, canManageVehicles, canManageTeam, isAdmin, isDealer } from '../../utils/permissions'
import { PERMISSIONS } from '../../utils/permissions'
import FeedbackModal from '../Common/FeedbackModal'
import MobileNavigation from './MobileNavigation'
import logo from '../../assets/logo.png'

const { Header, Sider } = Layout

const AppLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [feedbackVisible, setFeedbackVisible] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { userProfile, signOut, currentDealership, dealerships, switchDealership } = useAuth()
  const inactivityTimerRef = useRef(null)
  const siderRef = useRef(null)
  const collapsedRef = useRef(collapsed)

  // Inactivity timeout in milliseconds (5 seconds)
  const INACTIVITY_TIMEOUT = 5000

  // Keep ref in sync with state
  useEffect(() => {
    collapsedRef.current = collapsed
  }, [collapsed])

  const menuItems = useMemo(() => {
    const allMenuItems = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
      },
      {
        key: '/leads',
        icon: <UserOutlined />,
        label: 'Leads',
        permission: PERMISSIONS.VIEW_ALL_LEADS
      },
      {
        key: '/quotes',
        icon: <FileTextOutlined />,
        label: 'Quotes',
        permission: PERMISSIONS.VIEW_ALL_LEADS
      },
      {
        key: '/bookings',
        icon: <CalendarOutlined />,
        label: 'Bookings',
        permission: PERMISSIONS.VIEW_ALL_BOOKINGS
      },
      {
        key: '/customers',
        icon: <BulbOutlined />,
        label: 'Customers',
        permission: PERMISSIONS.VIEW_ALL_LEADS
      },
      {
        key: '/products',
        icon: <CarOutlined />,
        label: 'Vehicles',
        requireVehicleManagement: true
      },
      {
        key: '/dealers',
        icon: <ShopOutlined />,
        label: 'Dealers',
        requireDealerManagement: true
      },
      {
        key: '/pricing',
        icon: <DollarOutlined />,
        label: 'Pricing',
        permission: PERMISSIONS.VIEW_PRICING
      },
      {
        key: '/accessories',
        icon: <ShoppingCartOutlined />,
        label: 'Accessories',
        requireVehicleManagement: true
      },
      {
        key: '/dealer-inventory',
        icon: <InboxOutlined />,
        label: 'Accessories Inventory',
        requireDealer: true
      },
      {
        key: '/vehicle-inventory',
        icon: <CarOutlined />,
        label: 'Stock',
        requireDealer: true
      },
      {
        key: '/finance',
        icon: <WalletOutlined />,
        label: 'Banking Management',
        requireDealer: true
      },
      {
        key: '/bank-management',
        icon: <BankOutlined />,
        label: 'Bank Management',
        requireAdmin: true
      },
      {
        key: '/team',
        icon: <TeamOutlined />,
        label: 'Team',
        requireTeamManagement: true
      },
    ]

    // Filter menu items based on user permissions and remove custom props
    return allMenuItems.filter(item => {
      // Dashboard is always visible
      if (item.key === '/dashboard') {
        return true
      }

      // Check specific permission
      if (item.permission && !hasPermission(userProfile, item.permission)) {
        return false
      }

      // Check vehicle management requirement
      if (item.requireVehicleManagement && !canManageVehicles(userProfile)) {
        return false
      }

      // Check dealer management requirement
      if (item.requireDealerManagement && !canManageDealers(userProfile)) {
        return false
      }

      // Check team management requirement
      if (item.requireTeamManagement && !canManageTeam(userProfile)) {
        return false
      }

      // Check admin requirement
      if (item.requireAdmin && !isAdmin(userProfile)) {
        return false
      }

      // Check dealer only requirement
      if (item.requireDealer && !isDealer(userProfile)) {
        return false
      }

      return true
    }).map(item => {
      // Remove custom props before passing to Menu component
      const { requireVehicleManagement, requireDealerManagement, requireTeamManagement, requireAdmin, requireDealer, ...cleanItem } = item
      return cleanItem
    })
  }, [userProfile])

  const handleMenuClick = ({ key }) => {
    navigate(key)
  }

  const handleSignOut = () => {
    signOut()
    navigate('/login')
  }

  const handleSwitchDealership = (dealership) => {
    switchDealership(dealership)
  }

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
    }

    // Only set timer if sidebar is open
    if (!collapsedRef.current && siderRef.current) {
      inactivityTimerRef.current = setTimeout(() => {
        setCollapsed(true)
      }, INACTIVITY_TIMEOUT)
    }
  }, [])

  // Handle activity on sidebar
  const handleSidebarActivity = useCallback(() => {
    resetInactivityTimer()
  }, [resetInactivityTimer])

  // Set up inactivity detection for sidebar
  useEffect(() => {
    const siderElement = siderRef.current

    if (!siderElement) return

    // Only track activity when sidebar is open
    if (!collapsed) {
      // Reset timer on mount when sidebar is open
      resetInactivityTimer()

      // Track mouse movement
      const handleMouseMove = (e) => {
        // Check if mouse is over the sidebar
        if (siderElement.contains(e.target)) {
          resetInactivityTimer()
        }
      }

      // Track mouse clicks
      const handleClick = (e) => {
        // Check if click is on the sidebar
        if (siderElement.contains(e.target)) {
          resetInactivityTimer()
        }
      }

      // Track keyboard activity (when typing in search or navigating)
      const handleKeyDown = (e) => {
        // Check if focus is within sidebar
        if (siderElement.contains(document.activeElement)) {
          resetInactivityTimer()
        }
      }

      // Add event listeners
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('click', handleClick)
      document.addEventListener('keydown', handleKeyDown)

      // Cleanup
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('click', handleClick)
        document.removeEventListener('keydown', handleKeyDown)
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current)
        }
      }
    } else {
      // Clear timer when sidebar is collapsed
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
        inactivityTimerRef.current = null
      }
    }
  }, [collapsed])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
    }
  }, [])

  // Show dealership switcher for dealers and members with multiple dealerships
  const showDealershipSwitcher = !isAdmin(userProfile) && dealerships && dealerships.length > 1

  const userMenuItems = useMemo(() => {
    const items = [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: 'Profile',
        onClick: () => navigate('/profile')
      },
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: 'Settings',
        onClick: () => navigate('/settings')
      }
    ]

    // Add dealership switcher section if applicable
    if (showDealershipSwitcher) {
      items.push({
        type: 'divider',
      })

      items.push({
        key: 'dealership-header',
        label: (
          <div style={{
            fontWeight: '600',
            fontSize: '12px',
            color: '#8c8c8c',
            padding: '4px 0'
          }}>
            SWITCH DEALERSHIP
          </div>
        ),
        disabled: true
      })

      // Add dealership options
      dealerships.forEach(dealership => {
        items.push({
          key: `dealership-${dealership.id}`,
          icon: currentDealership?.id === dealership.id ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <ShopOutlined />,
          label: (
            <div>
              <div style={{ fontWeight: currentDealership?.id === dealership.id ? '600' : 'normal' }}>
                {dealership.name}
              </div>
              {dealership.address && typeof dealership.address === 'object' && (
                <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                  {dealership.address.city}{dealership.address.city && dealership.address.state ? ', ' : ''}{dealership.address.state}
                </div>
              )}
            </div>
          ),
          onClick: () => handleSwitchDealership(dealership),
          disabled: currentDealership?.dealershipId === dealership.dealershipId
        })
      })
    }

    items.push({
      type: 'divider',
    })

    items.push({
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: handleSignOut
    })

    return items
  }, [userProfile, dealerships, currentDealership, showDealershipSwitcher])

  return (
    <Layout style={{ height: '100vh' }}>
      <Sider
        ref={siderRef}
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          background: '#001529',
        }}
        onMouseEnter={handleSidebarActivity}
        onMouseMove={handleSidebarActivity}
        onClick={handleSidebarActivity}
      >
        <div style={{
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: collapsed ? '16px' : '20px',
          fontWeight: 'bold',
          borderBottom: '1px solid #002140',
          padding: '0 16px'
        }}>
          {collapsed ? (
            <img
              src={logo}
              alt="AUMS"
              style={{
                height: '32px',
                width: 'auto',
                filter: 'brightness(0) invert(1)'
              }}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img
                src={logo}
                alt="AUMS Logo"
                style={{
                  height: '32px',
                  width: 'auto'
                }}
              />
              <span>AUMS</span>
            </div>
          )}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout>
        <Header style={{
          padding: '0 24px',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => {
              const newCollapsed = !collapsed
              setCollapsed(newCollapsed)
              // Reset timer when opening sidebar
              if (newCollapsed === false) {
                setTimeout(() => {
                  resetInactivityTimer()
                }, 100)
              }
            }}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              type="text"
              icon={<BulbOutlined />}
              onClick={() => setFeedbackVisible(true)}
              style={{ fontSize: '18px' }}
              title="Share Feedback"
            />

            <Badge count={5} size="small">
              <Button
                type="text"
                icon={<BellOutlined />}
                style={{ fontSize: '18px' }}
              />
            </Badge>

            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '6px',
                transition: 'background-color 0.3s'
              }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#1890ff' }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{ fontWeight: '500', lineHeight: '1.2' }}>
                    {userProfile?.name || 'User'}
                  </span>
                  {currentDealership && !isAdmin(userProfile) && (
                    <span style={{ fontSize: '11px', color: '#8c8c8c', lineHeight: '1.2' }}>
                      {currentDealership.name}
                    </span>
                  )}
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>

        {children}
      </Layout>

      <FeedbackModal
        visible={feedbackVisible}
        onClose={() => setFeedbackVisible(false)}
      />

      <MobileNavigation />
    </Layout>
  )
}

export default AppLayout
